import type { ApolloClient } from '@apollo/client'

/**
 * WS auth recovery bridge. A subscription that fails with an expired JWT
 * re-subscribes over the SAME graphql-ws connection, whose token was captured
 * when the socket connected (`connectionParams` runs per connection, not per
 * subscribe) — so retrying alone can never recover. Recovery = terminate the
 * socket: graphql-ws reconnects and `connectionParams` (see `client.ts`)
 * hands it a fresh access token, refreshing first if the stored one expired.
 *
 * The terminator is keyed by the ApolloClient it belongs to. It used to be a
 * single module-level slot, which React StrictMode broke silently: the
 * client factory runs twice, the discarded client registered last, and
 * "recovery" terminated an idle socket while the live one kept its expired
 * token — every restart failed the same way, forever.
 */

const terminators = new WeakMap<ApolloClient, () => void>()

/** Called once per client by `client.ts` after creating the graphql-ws client. */
export function registerWsTerminate(client: ApolloClient, terminate: () => void): void {
  terminators.set(client, terminate)
}

/**
 * Force the ws connection of `client` to re-establish with a fresh token.
 * Synchronous; the reconnect + re-subscribe happen asynchronously after.
 * Safe to call repeatedly (a second terminate on an already-terminated socket
 * is a no-op inside graphql-ws).
 */
export function recoverWsAuth(client: ApolloClient): void {
  terminators.get(client)?.()
}
